import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getClientEnvironmentDetails } from '@sm/utils';
import { NavRouteParams } from '~app/pages/Pricing/components/Navigation/types';
import { PackageType } from '~shared/types/pricing/package';
import { DisplayFeatureStatic, SummaryDisplayFeatureList } from '~shared/types/pricing/features';
import { UniqueFeatureName } from '~pricing/components/FeatureSet/types';

import useFeatureSetStyles from '~app/pages/Pricing/components/FeatureSet/useStyles';
import FeatureTooltip from '~pricing/components/Tooltip';
import uniqueFeatureNameMap from '~pricing/components/FeatureSet/uniqueFeatureNameMap';
import styleOverrides from './useStyles';

function getUniqueFeatureName(featureName: string): string {
  const uniqueFeature = Object.keys(uniqueFeatureNameMap).find(feature => {
    return uniqueFeatureNameMap[feature as keyof UniqueFeatureName]?.includes(featureName) ? feature : null;
  });

  return uniqueFeature ?? featureName;
}

export default function FeatureSet({
  features,
  packageName,
  isLastPackage,
}: {
  features: SummaryDisplayFeatureList;
  packageName: string;
  isLastPackage: boolean;
}): React.ReactElement {
  const { canonicalPath } = useParams<NavRouteParams>();
  const { Features, FeatureItem, FeatureSummary, TooltipContainer, Highlight } = useFeatureSetStyles();
  const { FeatureItemOverride, EverythingIn } = styleOverrides();
  const { isMobile } = getClientEnvironmentDetails();

  const handleFeatureHover = useCallback(
    (featureName): void => {
      if (!isMobile) {
        document
          .querySelectorAll(`[data-feature='${featureName}']`)
          .forEach(feature => feature.classList.add(Highlight));
      }
    },
    [Highlight, isMobile]
  );

  const handleFeatureLeave = useCallback(
    (featureName): void => {
      if (!isMobile) {
        document
          .querySelectorAll(`[data-feature='${featureName}']`)
          .forEach(feature => feature.classList.remove(Highlight));
      }
    },
    [Highlight, isMobile]
  );

  return (
    <ul className={Features}>
      {features
        .filter((feature: DisplayFeatureStatic) => !!feature)
        .map((feature: DisplayFeatureStatic) => {
          const featureName = feature?.name && getUniqueFeatureName(feature?.name);
          return (
            <li
              className={`${canonicalPath === PackageType.Individual ? FeatureItemOverride : FeatureItem} ${
                featureName?.includes('everything_in') ? EverythingIn : ''
              }`}
              key={`${packageName}-feature-${feature?.summary}`}
              data-feature={featureName}
              onMouseEnter={handleFeatureHover.bind(null, featureName)}
              onMouseLeave={handleFeatureLeave.bind(null, featureName)}
            >
              {feature?.summary && (
                <span className={FeatureSummary}>
                  {feature?.summary}
                  {feature?.tooltip && feature?.tooltipHeading && (
                    <span className={TooltipContainer}>
                      <FeatureTooltip
                        tooltipHeading={feature.tooltipHeading}
                        tooltip={feature.tooltip}
                        isLastPackage={isLastPackage}
                      />
                    </span>
                  )}
                </span>
              )}
            </li>
          );
        })}
    </ul>
  );
}
